import React, { useState, useEffect } from 'react';
import { format, parse } from 'date-fns';

interface TimeSlotPickerProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

const TimeSlotPicker: React.FC<TimeSlotPickerProps> = ({ 
  value, 
  onChange,
  className = ""
}) => {
  // Parse the HH:mm value into hours and minutes
  const [hours, minutes] = value.split(':').map(Number);
  
  // Local state for the time picker
  const [localHours, setLocalHours] = useState(hours);
  const [localMinutes, setLocalMinutes] = useState(minutes);
  const [period, setPeriod] = useState(hours >= 12 ? 'PM' : 'AM');
  
  // Convert 24h to 12h format for display
  const displayHours = localHours % 12 === 0 ? 12 : localHours % 12;
  
  // Update local state when value prop changes
  useEffect(() => {
    const [newHours, newMinutes] = value.split(':').map(Number);
    setLocalHours(newHours);
    setLocalMinutes(newMinutes);
    setPeriod(newHours >= 12 ? 'PM' : 'AM');
  }, [value]);
  
  // Handle hour change
  const handleHourChange = (newHours: number) => {
    // Adjust for AM/PM
    let hours24 = newHours;
    if (period === 'PM' && newHours !== 12) {
      hours24 = newHours + 12;
    } else if (period === 'AM' && newHours === 12) {
      hours24 = 0;
    }
    
    setLocalHours(hours24);
    onChange(`${hours24.toString().padStart(2, '0')}:${localMinutes.toString().padStart(2, '0')}`);
  };
  
  // Handle minute change
  const handleMinuteChange = (newMinutes: number) => {
    setLocalMinutes(newMinutes);
    onChange(`${localHours.toString().padStart(2, '0')}:${newMinutes.toString().padStart(2, '0')}`);
  };
  
  // Handle period change (AM/PM)
  const handlePeriodChange = (newPeriod: 'AM' | 'PM') => {
    setPeriod(newPeriod);
    
    let hours24 = localHours;
    if (newPeriod === 'PM' && localHours < 12) {
      hours24 = localHours + 12;
    } else if (newPeriod === 'AM' && localHours >= 12) {
      hours24 = localHours - 12;
    }
    
    setLocalHours(hours24);
    onChange(`${hours24.toString().padStart(2, '0')}:${localMinutes.toString().padStart(2, '0')}`);
  };
  
  // Handle direct input for hours
  const handleHoursInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    if (inputValue === '') return;
    
    const newHours = parseInt(inputValue);
    if (isNaN(newHours) || newHours < 1 || newHours > 12) return;
    
    handleHourChange(newHours);
  };
  
  // Handle direct input for minutes
  const handleMinutesInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    if (inputValue === '') return;
    
    const newMinutes = parseInt(inputValue);
    if (isNaN(newMinutes) || newMinutes < 0 || newMinutes > 59) return;
    
    handleMinuteChange(newMinutes);
  };
  
  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      {/* Hours */}
      <div className="relative">
        <div className="flex flex-col items-center">
          <button 
            type="button"
            onClick={() => handleHourChange(displayHours === 12 ? 1 : displayHours + 1)}
            className="text-gray-500 hover:text-purple-600 focus:outline-none"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
          </button>
          <input
            type="text"
            inputMode="numeric"
            value={displayHours.toString().padStart(2, '0')}
            onChange={handleHoursInput}
            className="w-12 h-10 text-center text-xl font-semibold bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          />
          <button 
            type="button"
            onClick={() => handleHourChange(displayHours === 1 ? 12 : displayHours - 1)}
            className="text-gray-500 hover:text-purple-600 focus:outline-none"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
      
      <span className="text-xl font-bold">:</span>
      
      {/* Minutes */}
      <div className="relative">
        <div className="flex flex-col items-center">
          <button 
            type="button"
            onClick={() => handleMinuteChange((localMinutes + 15) % 60)}
            className="text-gray-500 hover:text-purple-600 focus:outline-none"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
          </button>
          <input
            type="text"
            inputMode="numeric"
            value={localMinutes.toString().padStart(2, '0')}
            onChange={handleMinutesInput}
            className="w-12 h-10 text-center text-xl font-semibold bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          />
          <button 
            type="button"
            onClick={() => handleMinuteChange((localMinutes - 15 + 60) % 60)}
            className="text-gray-500 hover:text-purple-600 focus:outline-none"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
      
      {/* AM/PM */}
      <div className="flex flex-col space-y-1">
        <button
          type="button"
          onClick={() => handlePeriodChange('AM')}
          className={`w-12 h-8 rounded-t-lg font-medium focus:outline-none ${
            period === 'AM' 
              ? 'bg-purple-600 text-white' 
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          AM
        </button>
        <button
          type="button"
          onClick={() => handlePeriodChange('PM')}
          className={`w-12 h-8 rounded-b-lg font-medium focus:outline-none ${
            period === 'PM' 
              ? 'bg-purple-600 text-white' 
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          PM
        </button>
      </div>
    </div>
  );
};

export default TimeSlotPicker;